import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiErrorCode } from '../../../Api';
import Button from '../../../ui/Button';
import ItemGrid from '../../../ui/ItemGrid';
import ItemSlot from '../../../ui/ItemSlot';
import { ItemArtwork, ItemQuantity } from '../ItemSlotContent';
import { MEDIA_VARIANTS } from '../../../media';
import GameAPI from '../GameAPI';
import { GameItemTooltip } from '../Game_ItemTooltip';
import { ImageLoader } from '../ImageLoader';
import GroundLootDialogFrame from '../GroundLoot/GroundLootDialogFrame';
import { isTouchPointer } from '../touchItemDrag';
import './BaseCamp.css';

const PACK_CAPACITY = 25;
const PACK_WIDTH = 5;
const EMPTY_ENTRIES = [];
const splitQuantity = quantity => Math.floor((Number(quantity) || 0) / 2);
const recoveryTierLabel = tier => ({
  unknown: 'Unknown',
  risky: 'Risky',
  practiced: 'Practiced',
  skilled: 'Skilled',
  mastered: 'Mastered',
}[String(tier || '').toLowerCase()] || '');

function ItemTransfer({
  busy,
  direction,
  entry,
  item,
  onDragEnd,
  onDragStart,
  onSelect,
  onSalvageItem,
  onTransfer,
  salvageMode = false,
  selected = false,
}) {
  const anchorRef = useRef(null);
  const suppressClickRef = useRef(false);
  const tooltipPinnedRef = useRef(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipInteractive, setTooltipInteractive] = useState(false);
  const itemName = item?.name || entry.itemId || 'Item';
  const toStorage = direction === 'packToStorage';
  const destination = toStorage ? 'Vault' : 'Pack';
  const transfer = {
    direction,
    itemId: entry.itemId,
    itemInstanceId: entry.itemInstanceId || '',
    quantity: Number(entry.quantity) || 1,
    ...(toStorage
      ? {
        sourceX: Number(entry.x),
        sourceY: Number(entry.y),
      }
      : {
        sourceSlotIndex: Number(entry.slotIndex),
      }),
  };
  const closeTooltip = () => {
    tooltipPinnedRef.current = false;
    setTooltipInteractive(false);
    setTooltipVisible(false);
  };
  const showTooltip = interactive => {
    if (!interactive && tooltipPinnedRef.current) return;
    tooltipPinnedRef.current = interactive;
    setTooltipInteractive(interactive);
    setTooltipVisible(true);
  };
  return (
    <>
      <div
        className={`camp-storage-item${selected ? ' camp-storage-item--selected' : ''}`}
        onBlur={event => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          if (!tooltipPinnedRef.current) closeTooltip();
        }}
        onFocus={() => {
          showTooltip(false);
        }}
        onMouseEnter={() => {
          showTooltip(false);
        }}
        onMouseLeave={() => {
          if (!tooltipPinnedRef.current) closeTooltip();
        }}
        onPointerLeave={() => {
          if (!tooltipPinnedRef.current) closeTooltip();
        }}
        onPointerMove={event => {
          if (!isTouchPointer(event)) showTooltip(false);
        }}
      >
        <button
          aria-label={salvageMode
            ? `Select ${itemName} for salvage, quantity ${entry.quantity}`
            : `Move ${itemName} to ${destination}, quantity ${entry.quantity}`}
          className="camp-storage-item__main"
          disabled={busy}
          draggable={!busy}
          onClick={() => {
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              return;
            }
            if (selected) {
              onDragEnd?.();
              return;
            }
            closeTooltip();
            if (salvageMode) onSelect(transfer);
            else onTransfer(transfer);
          }}
          onContextMenu={event => {
            event.preventDefault();
            showTooltip(true);
          }}
          onDragEnd={onDragEnd}
          onDragStart={event => {
            closeTooltip();
            if (event.dataTransfer) {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', entry.itemInstanceId || entry.itemId);
            }
            const half = splitQuantity(entry.quantity);
            onDragStart(event.shiftKey && half > 0
              ? { ...transfer, quantity: half }
              : transfer);
          }}
          onPointerUp={event => {
            if (!isTouchPointer(event)) return;
            suppressClickRef.current = true;
            showTooltip(true);
          }}
          ref={anchorRef}
          title={salvageMode
            ? 'Select, then place this equipment on the shredder'
            : toStorage
            ? 'Click or drag to move to Vault; Shift-drag moves half'
            : 'Click to move to Pack; drag to a Pack or Vault slot; Shift-drag moves half'}
          type="button"
        >
          <ItemArtwork
            item={item}
            itemId={entry.itemId}
          />
        </button>
        {!salvageMode && splitQuantity(entry.quantity) > 0 ? (
          <ItemQuantity
            ariaLabel={`Pick up half of ${itemName}, ${splitQuantity(entry.quantity)} of ${entry.quantity}`}
            disabled={busy}
            onClick={event => {
              event.stopPropagation();
              closeTooltip();
              onDragStart({
                ...transfer,
                quantity: splitQuantity(entry.quantity),
              });
            }}
            quantity={entry.quantity}
            title="Pick up half this stack"
          />
        ) : (
          <ItemQuantity quantity={entry.quantity} />
        )}
      </div>
      {tooltipVisible ? (
        <GameItemTooltip
          actions={salvageMode ? [{
            id: 'salvage',
            label: 'Select for salvage',
            disabled: busy || !entry.itemInstanceId,
            disabledReason: !entry.itemInstanceId
              ? 'Only equipment can be salvaged.'
              : busy ? 'Another Camp item action is in progress.' : '',
            onSelect: () => {
              closeTooltip();
              onSalvageItem?.(transfer);
            },
          }] : [{
            id: 'transfer',
            label: `Move to ${destination}`,
            disabled: busy,
            disabledReason: busy ? 'Another Camp item action is in progress.' : '',
            onSelect: () => {
              closeTooltip();
              onTransfer(transfer);
            },
          }, {
            id: 'move',
            label: 'Choose destination',
            disabled: busy,
            disabledReason: busy ? 'Another Camp item action is in progress.' : '',
            onSelect: () => {
              closeTooltip();
              onSelect(transfer);
            },
          }, ...(splitQuantity(entry.quantity) > 0 ? [{
            id: 'split',
            label: 'Split half',
            disabled: busy,
            disabledReason: busy ? 'Another Camp item action is in progress.' : '',
            onSelect: () => {
              closeTooltip();
              onDragStart({ ...transfer, quantity: splitQuantity(entry.quantity) });
            },
          }] : [])]}
          anchorRect={anchorRef.current?.getBoundingClientRect()}
          getAnchorElement={() => anchorRef.current}
          getAnchorRect={() => anchorRef.current?.getBoundingClientRect()}
          itemId={entry.itemId}
          itemInstanceId={entry.itemInstanceId || ''}
          interactive={tooltipInteractive}
          onRequestClose={closeTooltip}
        />
      ) : null}
    </>
  );
}

function StorageGrid({
  busy,
  capacity,
  entries,
  itemsLoadedAt,
  onActivate,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSalvageItem,
  onTransfer,
  onSelect,
  salvageMode,
  selectedTransfer,
}) {
  const slots = Array.from({ length: Math.max(0, capacity) }, () => null);
  entries.forEach(entry => {
    if (entry.slotIndex >= 0 && entry.slotIndex < slots.length) {
      slots[entry.slotIndex] = entry;
    }
  });

  return (
    <>
      <ItemGrid
        className="camp-storage-grid"
        columns={PACK_WIDTH}
        data-items-loaded-at={itemsLoadedAt || undefined}
        fluid
        label="Vault slots"
        rows={Math.ceil(slots.length / PACK_WIDTH)}
      >
        {slots.map((entry, slotIndex) => {
          const item = entry
            ? GameAPI.fetchItemAutoCached(entry.itemId, entry.itemInstanceId)
            : null;
          const rarity = item && GameAPI.fetchRarityCached(item.rarityId);
          const selected = Boolean(
            entry
            && selectedTransfer?.sourceSlotIndex === entry.slotIndex
            && selectedTransfer?.direction === 'storageToPack'
          );
          return (
            <ItemSlot
              aria-colindex={(slotIndex % PACK_WIDTH) + 1}
              aria-label={`Vault slot ${slotIndex + 1}${item ? `, ${item.name}` : ', empty'}`}
              aria-rowindex={Math.floor(slotIndex / PACK_WIDTH) + 1}
              aria-selected={selected || undefined}
              className="camp-storage-slot"
              item={item}
              itemType={GameAPI.fetchItemTypeCached(item?.typeId)}
              key={`camp-storage-${slotIndex}`}
              onClickCapture={event => {
                if (onActivate(slotIndex)) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              onDragOver={onDragOver}
              onDrop={event => onDrop(event, slotIndex)}
              onKeyDown={event => {
                if (
                  selectedTransfer
                  && (event.key === 'Enter' || event.key === ' ')
                  && onActivate(slotIndex)
                ) {
                  event.preventDefault();
                }
              }}
              rarity={rarity?.name?.toLowerCase()}
              rarityColor={rarity?.colorHex}
              role="gridcell"
              selected={selected}
              tabIndex={selectedTransfer ? 0 : -1}
            >
              {entry ? (
                <ItemTransfer
                  busy={busy}
                  direction="storageToPack"
                  entry={entry}
                  item={item}
                  onDragEnd={onDragEnd}
                  onDragStart={onDragStart}
                  onSalvageItem={onSalvageItem}
                  onSelect={onSelect}
                  onTransfer={onTransfer}
                  salvageMode={salvageMode}
                  selected={selected}
                />
              ) : null}
            </ItemSlot>
          );
        })}
      </ItemGrid>
    </>
  );
}

function PackGrid({
  busy,
  inventory,
  onActivate,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSalvageItem,
  onTransfer,
  onSelect,
  salvageMode,
  selectedTransfer,
}) {
  const slots = Array.from({ length: PACK_CAPACITY }, () => null);
  inventory.forEach(entry => {
    const index = (Number(entry.x) * PACK_WIDTH) + Number(entry.y);
    if (index >= 0 && index < slots.length) slots[index] = entry;
  });

  return (
    <>
      <ItemGrid
        className="camp-storage-grid"
        columns={PACK_WIDTH}
        fluid
        label="Pack slots for Vault transfers"
        rows={PACK_CAPACITY / PACK_WIDTH}
      >
        {slots.map((entry, slotIndex) => {
          const item = entry
            ? GameAPI.fetchItemAutoCached(entry.itemId, entry.itemInstanceId)
            : null;
          const rarity = item && GameAPI.fetchRarityCached(item.rarityId);
          const selected = Boolean(
            entry
            && selectedTransfer?.sourceX === entry.x
            && selectedTransfer?.sourceY === entry.y
            && selectedTransfer?.direction === 'packToStorage'
          );
          return (
            <ItemSlot
              aria-colindex={(slotIndex % PACK_WIDTH) + 1}
              aria-label={`Pack slot ${slotIndex + 1}${item ? `, ${item.name}` : ', empty'}`}
              aria-rowindex={Math.floor(slotIndex / PACK_WIDTH) + 1}
              aria-selected={selected || undefined}
              className="camp-storage-slot"
              item={item}
              itemType={GameAPI.fetchItemTypeCached(item?.typeId)}
              key={`camp-pack-${slotIndex}`}
              onClickCapture={event => {
                if (onActivate(
                  Math.floor(slotIndex / PACK_WIDTH),
                  slotIndex % PACK_WIDTH,
                )) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              onDragOver={onDragOver}
              onDrop={event => onDrop(
                event,
                Math.floor(slotIndex / PACK_WIDTH),
                slotIndex % PACK_WIDTH,
              )}
              onKeyDown={event => {
                if (
                  selectedTransfer
                  && (event.key === 'Enter' || event.key === ' ')
                  && onActivate(
                    Math.floor(slotIndex / PACK_WIDTH),
                    slotIndex % PACK_WIDTH,
                  )
                ) {
                  event.preventDefault();
                }
              }}
              rarity={rarity?.name?.toLowerCase()}
              rarityColor={rarity?.colorHex}
              role="gridcell"
              selected={selected}
              tabIndex={selectedTransfer ? 0 : -1}
            >
              {entry ? (
                <ItemTransfer
                  busy={busy}
                  direction="packToStorage"
                  entry={entry}
                  item={item}
                  onDragEnd={onDragEnd}
                  onDragStart={onDragStart}
                  onSalvageItem={onSalvageItem}
                  onSelect={onSelect}
                  onTransfer={onTransfer}
                  salvageMode={salvageMode}
                  selected={selected}
                />
              ) : null}
            </ItemSlot>
          );
        })}
      </ItemGrid>
      {inventory.length === 0 ? (
        <p className="camp-storage__empty">Your Pack is empty.</p>
      ) : null}
    </>
  );
}

export function CampStorageWorkspace({
  busy = false,
  error = '',
  inventory = EMPTY_ENTRIES,
  loading = false,
  onRefresh,
  onTransfer,
  onSalvage,
  onClose,
  mode = 'storage',
  storage,
}) {
  const componentId = useId().replace(/:/g, '');
  const storageTitleId = `camp-storage-heading-${componentId}`;
  const packTitleId = `camp-pack-heading-${componentId}`;
  const [itemsLoadedAt, setItemsLoadedAt] = useState(0);
  const [itemDetailsError, setItemDetailsError] = useState('');
  const [draggedTransfer, setDraggedTransfer] = useState(null);
  const [salvaging, setSalvaging] = useState(false);
  const [salvageYields, setSalvageYields] = useState(null);
  const [salvageRecipeProgress, setSalvageRecipeProgress] = useState(null);
  const [salvageRecoveryTier, setSalvageRecoveryTier] = useState('');
  const [updatedRecoveryTier, setUpdatedRecoveryTier] = useState('');
  const [salvageResultFading, setSalvageResultFading] = useState(false);
  const [salvagePreview, setSalvagePreview] = useState(null);
  const [salvageDropPending, setSalvageDropPending] = useState(false);
  const [starterRemovalPending, setStarterRemovalPending] = useState(null);
  const previewRequestRef = useRef(0);
  const salvageResultFadeRef = useRef(null);
  const salvageMode = mode === 'salvage';
  const workspaceBusy = busy || loading;
  const entries = useMemo(
    () => storage?.entries || EMPTY_ENTRIES,
    [storage?.entries],
  );
  const capacity = Math.max(
    entries.length,
    Number(storage?.capacity) || 20,
  );
  const starterRemovalItem = starterRemovalPending
    ? GameAPI.fetchItemAutoCached(
      starterRemovalPending.itemId,
      starterRemovalPending.itemInstanceId,
    )
    : null;
  const salvageRecipeText = salvageRecipeProgress
    ? (salvageRecipeProgress.learned ?? salvageRecipeProgress.Learned)
      ? `Item level ${salvageRecipeProgress.learnedItemLevel
        ?? salvageRecipeProgress.LearnedItemLevel} learned!`
      : `Reverse engineering: ${salvageRecipeProgress.salvageCount
        ?? salvageRecipeProgress.SalvageCount}/${salvageRecipeProgress.nextLevelRequiredCount
        ?? salvageRecipeProgress.NextLevelRequiredCount
        ?? salvageRecipeProgress.requiredCount
        ?? salvageRecipeProgress.RequiredCount}`
    : '';
  const previewTier = recoveryTierLabel(salvagePreview?.recoveryTier);
  const previewReady = Boolean(
    draggedTransfer?.itemInstanceId
      && !salvagePreview?.loading
      && !salvagePreview?.error
      && (previewTier || salvagePreview?.starterRemovalOnly),
  );
  const clearSelection = () => {
    previewRequestRef.current += 1;
    setSalvageDropPending(false);
    setStarterRemovalPending(null);
    setDraggedTransfer(null);
    setSalvagePreview(null);
  };
  const previewTransfer = async transfer => {
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setSalvagePreview({ loading: true, error: '' });
    try {
      const result = await GameAPI.previewSalvage({
        itemId: transfer.itemId,
        itemInstanceId: transfer.itemInstanceId,
        ...(transfer.direction === 'storageToPack'
          ? {
            source: 'vault',
            sourceSlotIndex: transfer.sourceSlotIndex,
          }
          : {
            source: 'pack',
            x: transfer.sourceX,
            y: transfer.sourceY,
          }),
      });
      if (previewRequestRef.current !== requestId) return;
      setSalvagePreview({
        loading: false,
        error: '',
        recoveryTier: result?.recoveryTier ?? result?.RecoveryTier ?? '',
        starterRemovalOnly: Boolean(
          result?.starterRemovalOnly ?? result?.StarterRemovalOnly,
        ),
      });
    } catch (previewError) {
      if (previewRequestRef.current !== requestId) return;
      setSalvagePreview({
        loading: false,
        error: previewError?.message || 'Recovery outlook is unavailable.',
      });
    }
  };
  const selectTransfer = transfer => {
    if (salvageYields !== null) {
      clearTimeout(salvageResultFadeRef.current);
      setSalvageResultFading(true);
      salvageResultFadeRef.current = setTimeout(() => {
        setSalvageYields(null);
        setSalvageResultFading(false);
      }, 500);
    }
    setSalvageDropPending(false);
    setStarterRemovalPending(null);
    setDraggedTransfer(transfer);
    if (salvageMode && transfer?.itemInstanceId) {
      previewTransfer(transfer);
    }
  };
  const allowDrop = directions => event => {
    if (!workspaceBusy && directions.includes(draggedTransfer?.direction)) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    }
  };
  const allowSalvageDrop = event => {
    if (!workspaceBusy && draggedTransfer?.itemInstanceId) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    }
  };
  const dropOnSalvage = event => {
    allowSalvageDrop(event);
    if (!workspaceBusy && draggedTransfer?.itemInstanceId) {
      setSalvageDropPending(true);
    }
  };
  const transferToStorage = destinationSlotIndex => {
    if (
      workspaceBusy
      || !['packToStorage', 'storageToPack']
        .includes(draggedTransfer?.direction)
    ) return false;
    if (draggedTransfer.direction === 'packToStorage') {
      onTransfer({ ...draggedTransfer, destinationSlotIndex });
    } else if (draggedTransfer.sourceSlotIndex !== destinationSlotIndex) {
      onTransfer({
        ...draggedTransfer,
        direction: 'storageToStorage',
        destinationSlotIndex,
      });
    } else {
      setDraggedTransfer(null);
      return true;
    }
    setDraggedTransfer(null);
    return true;
  };
  const dropOnStorage = (event, destinationSlotIndex) => {
    if (!transferToStorage(destinationSlotIndex)) return;
    event.preventDefault();
  };
  const transferToPack = (destinationX, destinationY) => {
    if (
      workspaceBusy
      || draggedTransfer?.direction !== 'storageToPack'
    ) return false;
    onTransfer({ ...draggedTransfer, destinationX, destinationY });
    setDraggedTransfer(null);
    return true;
  };
  const dropOnPack = (event, destinationX, destinationY) => {
    if (!transferToPack(destinationX, destinationY)) return;
    event.preventDefault();
  };
  const salvageSelected = useCallback(async (
    event,
    confirmStarterRemoval = false,
    requestedTransfer = null,
  ) => {
    event?.preventDefault?.();
    const transfer = confirmStarterRemoval
      ? starterRemovalPending
      : requestedTransfer || draggedTransfer;
    if (
      workspaceBusy
      || salvaging
      || !transfer?.itemInstanceId
      || !onSalvage
      || salvagePreview?.loading
      || salvagePreview?.error
      || (!salvagePreview?.recoveryTier
        && !salvagePreview?.starterRemovalOnly)
    ) return;
    clearTimeout(salvageResultFadeRef.current);
    setSalvaging(true);
    setSalvageResultFading(false);
    setSalvageYields(null);
    setSalvageRecipeProgress(null);
    setSalvageRecoveryTier('');
    setUpdatedRecoveryTier('');
    try {
      const result = await onSalvage({
        itemId: transfer.itemId,
        itemInstanceId: transfer.itemInstanceId,
        ...(confirmStarterRemoval ? { confirmStarterRemoval: true } : {}),
        ...(transfer.direction === 'storageToPack'
          ? {
            source: 'vault',
            sourceSlotIndex: transfer.sourceSlotIndex,
          }
          : {
            source: 'pack',
            x: transfer.sourceX,
            y: transfer.sourceY,
          }),
      });
      const yields = result?.yields ?? result?.Yields;
      const recipeProgress = result?.recipeProgress ?? result?.RecipeProgress;
      const recoveryTier = result?.recoveryTier ?? result?.RecoveryTier;
      const nextRecoveryTier = result?.updatedRecoveryTier
        ?? result?.UpdatedRecoveryTier;
      setSalvageYields(Array.isArray(yields) ? yields : []);
      setSalvageRecipeProgress(recipeProgress || null);
      setSalvageRecoveryTier(recoveryTierLabel(recoveryTier));
      setUpdatedRecoveryTier(recoveryTierLabel(nextRecoveryTier));
      setStarterRemovalPending(null);
      setDraggedTransfer(null);
      setSalvagePreview(null);
    } catch (error) {
      if (apiErrorCode(error)
        === 'STARTER_REMOVAL_CONFIRMATION_REQUIRED') {
        setStarterRemovalPending(transfer);
        return;
      }
      throw error;
    } finally {
      setSalvaging(false);
    }
  }, [
    draggedTransfer,
    onSalvage,
    salvagePreview,
    salvaging,
    starterRemovalPending,
    workspaceBusy,
  ]);

  useEffect(() => {
    if (!salvageDropPending || salvagePreview?.loading) return;
    setSalvageDropPending(false);
    if (previewReady) salvageSelected();
  }, [previewReady, salvageDropPending, salvagePreview?.loading, salvageSelected]);

  useEffect(() => () => clearTimeout(salvageResultFadeRef.current), []);

  useEffect(() => {
    let active = true;
    setItemDetailsError('');
    const itemRefs = [...entries, ...inventory].map(entry => ({
      itemId: entry.itemId,
      itemInstanceId: entry.itemInstanceId || '',
    }));
    GameAPI.fetchItemsAuto(itemRefs)
      .then(() => GameAPI.fetchRarities(
        itemRefs.map(({ itemId, itemInstanceId }) => (
          GameAPI.fetchItemAutoCached(itemId, itemInstanceId)?.rarityId
        )),
      ))
      .catch(error => {
        console.error('Failed to load Camp storage item details', error);
        if (active) {
          setItemDetailsError(
            'Some item details are unavailable. Transfers still work.',
          );
        }
      })
      .finally(() => {
        if (active) {
          setItemsLoadedAt(current => Math.max(Date.now(), current + 1));
        }
      });
    return () => { active = false; };
  }, [entries, inventory]);

  return (
    <div
      aria-busy={busy || salvaging || loading || undefined}
      className="camp-storage-workspace"
      onKeyDown={event => {
        if (event.key === 'Escape'
          && (draggedTransfer || starterRemovalPending)) {
          event.preventDefault();
          setStarterRemovalPending(null);
          clearSelection();
        }
      }}
    >
      {error ? <p className="camp-storage__error" role="alert">{error}</p> : null}
      {itemDetailsError ? (
        <p className="camp-storage__error" role="status">
          {itemDetailsError}
        </p>
      ) : null}
      {loading && !storage ? (
        <p aria-busy="true" className="camp-storage__loading" role="status">
          Refreshing Vault…
        </p>
      ) : (
        <div className="camp-storage-layout">
          {salvageMode ? (
            <div className="camp-salvage-station">
              {salvageYields ? (
                <div
                  className={`camp-salvage-result${salvageResultFading ? ' camp-salvage-result--fading' : ''}`}
                  role="status"
                >
                  <strong>
                    <span>Extracted</span>
                    {salvageRecipeText ? (
                      <span className="camp-salvage-result__recipe">
                        {salvageRecipeText}
                      </span>
                    ) : null}
                    {salvageRecoveryTier ? (
                      <span className="camp-salvage-result__recipe">
                        Recovery: {salvageRecoveryTier}
                        {updatedRecoveryTier
                          && updatedRecoveryTier !== salvageRecoveryTier
                          ? ` · Item knowledge improved to ${updatedRecoveryTier}`
                          : ''}
                      </span>
                    ) : null}
                  </strong>
                  {salvageYields.length > 0 ? (
                    <ul className="camp-salvage-result__list">
                      {salvageYields.map(entry => {
                        const itemId = entry?.itemId ?? entry?.ItemId ?? '';
                        const quantity = entry?.quantity ?? entry?.Quantity ?? 0;
                        const item = GameAPI.fetchItemAutoCached(itemId, '');
                        return (
                          <li key={itemId}>
                            <ImageLoader
                              alt=""
                              animatedMediaId={item?.animatedMediaId || item?.animatedImageId || ''}
                              className="camp-salvage-result__image"
                              imageId={item?.imageId || ''}
                              itemId={itemId}
                              staticPreview
                              variant={MEDIA_VARIANTS.ENTITY_ICON}
                            />
                            <span>{quantity}× {item?.name || itemId}</span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <span className="camp-salvage-result__empty">
                      Item removed — no materials extracted.
                    </span>
                  )}
                </div>
              ) : (
                <div
                  className="camp-salvage-drop-target"
                  onDragOver={allowSalvageDrop}
                  onDrop={dropOnSalvage}
                >
                  <button
                    aria-label="Salvage selected equipment"
                    className={`camp-salvage-shredder${previewReady && !starterRemovalPending ? ' camp-salvage-shredder--ready' : ''}`}
                    disabled={workspaceBusy || salvaging || starterRemovalPending || !previewReady}
                    onClick={salvageSelected}
                    type="button"
                  >
                    <strong>{salvaging ? 'Shredding…' : 'Shredder'}</strong>
                    {salvagePreview?.loading ? (
                      <span>Assessing item knowledge…</span>
                    ) : salvagePreview?.error ? (
                      <span className="camp-salvage-preview-error">
                        Recovery outlook unavailable. Reselect the item.
                      </span>
                    ) : salvagePreview?.starterRemovalOnly ? (
                      <span>Removal only — Starter equipment returns no materials.</span>
                    ) : previewTier ? (
                      <span>
                        Recovery outlook: <strong>{previewTier}</strong>
                      </span>
                    ) : (
                      <span>Select equipment to assess its recovery outlook.</span>
                    )}
                    <span>
                      Item knowledge and permanent Salvage Skill improve recovery.
                    </span>
                  </button>
                </div>
              )}
              {starterRemovalPending ? (
                <div className="camp-salvage-confirmation" role="alert">
                  <span>
                    <strong>
                      Remove {starterRemovalItem?.name
                        || starterRemovalPending.itemId}?
                    </strong>
                    Starter equipment returns no materials.
                  </span>
                  <div>
                    <Button
                      disabled={workspaceBusy || salvaging}
                      onClick={event => salvageSelected(event, true)}
                      variant="danger"
                    >
                      Remove item
                    </Button>
                    <Button
                      disabled={workspaceBusy || salvaging}
                      onClick={() => {
                        clearSelection();
                      }}
                      variant="secondary"
                    >
                      Keep item
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <section aria-labelledby={storageTitleId} className="camp-storage-panel">
            <header className="camp-storage-panel__heading">
              <h3 id={storageTitleId}>Vault</h3>
              <span>{entries.length} / {capacity}</span>
            </header>
            <StorageGrid
              busy={workspaceBusy}
              capacity={capacity}
              entries={entries}
              itemsLoadedAt={itemsLoadedAt}
              onActivate={transferToStorage}
              onDragEnd={event => {
                if (!salvageMode || !event) clearSelection();
              }}
              onDragOver={allowDrop(['packToStorage', 'storageToPack'])}
              onDragStart={selectTransfer}
              onDrop={dropOnStorage}
              onSalvageItem={selectTransfer}
              onTransfer={onTransfer}
              onSelect={selectTransfer}
              salvageMode={salvageMode}
              selectedTransfer={draggedTransfer}
            />
          </section>

          <section aria-labelledby={packTitleId} className="camp-storage-panel">
            <header className="camp-storage-panel__heading">
              <h3 id={packTitleId}>Your Pack</h3>
              <span>{inventory.length} / {PACK_CAPACITY}</span>
            </header>
            <PackGrid
              busy={workspaceBusy}
              inventory={inventory}
              onActivate={transferToPack}
              onDragEnd={event => {
                if (!salvageMode || !event) clearSelection();
              }}
              onDragOver={allowDrop(['storageToPack'])}
              onDragStart={selectTransfer}
              onDrop={dropOnPack}
              onSalvageItem={selectTransfer}
              onTransfer={onTransfer}
              onSelect={selectTransfer}
              salvageMode={salvageMode}
              selectedTransfer={draggedTransfer}
            />
          </section>
        </div>
      )}

      <footer className="camp-storage__footer">
        <span aria-live="polite" role="status">
          {salvaging
            ? 'Salvaging equipment…'
            : loading
            ? 'Refreshing Vault…'
            : starterRemovalPending
            ? 'Choose whether to remove or keep the Starter equipment.'
            : salvagePreview?.loading
            ? 'Assessing the selected equipment…'
            : salvagePreview?.error
            ? 'Recovery outlook is unavailable. Reselect the equipment.'
            : previewTier
            ? `Recovery outlook: ${previewTier}. Activate the shredder to continue.`
            : salvageMode
            ? 'Select equipment to assess it before activating the shredder.'
            : draggedTransfer
            ? `${draggedTransfer.quantity} selected. Choose a compatible destination.`
            : busy ? 'Transferring item…' : 'Transfers are saved by the server.'}
        </span>
        <div>
          <Button
            disabled={busy || loading}
            onClick={onRefresh}
            variant="ghost"
          >
            Refresh
          </Button>
          {onClose ? (
            <Button disabled={busy} onClick={onClose} variant="secondary">
              Close
            </Button>
          ) : null}
        </div>
      </footer>
    </div>
  );
}

export default function CampStorageDialog(props) {
  return (
    <GroundLootDialogFrame
      busy={props.busy}
      className="camp-storage-dialog"
      description="Move, merge, or split item stacks between your Pack and the run-safe Vault."
      eyebrow="Run-safe storage"
      onClose={props.onClose}
      title="Vault"
    >
      <CampStorageWorkspace {...props} />
    </GroundLootDialogFrame>
  );
}
